import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { authService } from '../../services/authService';
import { Button } from '../common/Button';
import { useLanguage } from '../../features/language/useLanguage';
import { getErrorMessage } from '../../utils/error';

type TFunction = (key: string, params?: Record<string, string | number>, fallback?: string) => string;

const buildSchema = (t: TFunction) =>
  z
    .object({
      currentPassword: z.string().min(1, t('settings.changePassword.errors.currentRequired')),
      newPassword: z.string().min(6, t('settings.changePassword.errors.newMinLength')),
      confirmPassword: z.string().min(6, t('settings.changePassword.errors.newMinLength')),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t('settings.changePassword.errors.confirmMismatch'),
      path: ['confirmPassword'],
    })
    .refine((data) => data.newPassword !== data.currentPassword, {
      message: t('settings.changePassword.errors.sameAsCurrent'),
      path: ['newPassword'],
    });

type ChangePasswordFormData = z.infer<ReturnType<typeof buildSchema>>;

export const ChangePasswordForm: React.FC = () => {
  const { t } = useLanguage();
  const schema = useMemo(() => buildSchema(t), [t]);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [rootError, setRootError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (data: ChangePasswordFormData) => {
    setRootError(null);
    setIsSuccess(false);
    try {
      await authService.changePassword(data.currentPassword, data.newPassword);
      setIsSuccess(true);
      reset();
      setTimeout(() => setIsSuccess(false), 3000);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const apiError = (err.response?.data as { error?: string } | undefined)?.error;
        if (apiError === 'Current password is incorrect') {
          setRootError(t('settings.changePassword.errors.currentIncorrect'));
          return;
        }
      }
      setRootError(getErrorMessage(err) || t('settings.changePassword.errors.updateFailed'));
    }
  };

  const inputClass =
    'block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 pr-10';

  return (
    <div className="bg-white shadow sm:rounded-lg" data-test-id="settings-change-password">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          {t('settings.changePassword.title')}
        </h3>
        <div className="mt-2 max-w-xl text-sm text-gray-500">
          <p>{t('settings.changePassword.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4 sm:border-t sm:border-gray-200 sm:pt-6">
          {rootError && (
            <div
              className="p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm"
              data-test-id="settings-change-password-error"
            >
              {rootError}
            </div>
          )}

          <div className="w-full sm:w-[30rem]">
            <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
              {t('settings.changePassword.currentPassword')}
            </label>
            <div className="relative mt-1">
              <input
                id="currentPassword"
                type={showCurrent ? 'text' : 'password'}
                autoComplete="current-password"
                data-test-id="settings-change-password-current-input"
                className={inputClass}
                {...register('currentPassword')}
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowCurrent((v) => !v)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.currentPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.currentPassword.message}</p>
            )}
          </div>

          <div className="w-full sm:w-[30rem]">
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
              {t('settings.changePassword.newPassword')}
            </label>
            <div className="relative mt-1">
              <input
                id="newPassword"
                type={showNew ? 'text' : 'password'}
                autoComplete="new-password"
                data-test-id="settings-change-password-new-input"
                className={inputClass}
                {...register('newPassword', { deps: ['confirmPassword'] })}
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowNew((v) => !v)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.newPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.newPassword.message}</p>
            )}
          </div>

          <div className="w-full sm:w-[30rem]">
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
              {t('settings.changePassword.confirmPassword')}
            </label>
            <div className="relative mt-1">
              <input
                id="confirmPassword"
                type={showConfirm ? 'text' : 'password'}
                autoComplete="new-password"
                data-test-id="settings-change-password-confirm-input"
                className={inputClass}
                {...register('confirmPassword')}
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            {isSuccess && (
              <span className="text-sm text-green-600" data-test-id="settings-change-password-success">
                {t('settings.changePassword.success')}
              </span>
            )}
            <Button
              type="submit"
              data-test-id="settings-change-password-submit"
              isLoading={isSubmitting}
            >
              {isSubmitting ? t('settings.changePassword.submitting') : t('settings.changePassword.submit')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
